// <WebGLRenderer> — FXC-8685 (Warp at scale).
//
// A WebGL GraphRenderer (Sigma.js + graphology) for large single-view
// neighbourhoods that the canvas `force` renderer can't handle (~2-5k ceiling).
// Sigma draws points/edges in WebGL with a CPU-side ForceAtlas2 layout — it's
// comfortable to ~50-100k nodes; layout (not draw) is the ceiling, so big graphs
// run bounded FA2 iterations. Honours the GraphRenderer contract: colours via
// colourForType/nodeColor/colourForPredicate, highlightIds/searchActive dimming,
// click/hover/right-click, and onReady.zoomToFit. Cluster `groups` hulls are
// drawn on a 2D overlay reusing the package's convexHull/drawGroupHull.
//
// flowParticles has no Sigma-native equivalent and is a graceful no-op here (as
// in HierarchicalRenderer) — use the `force` renderer when particles matter.

import { useEffect, useMemo, useRef } from "react";
import Graphology from "graphology";
import Sigma from "sigma";
import forceAtlas2 from "graphology-layout-forceatlas2";

import { colourFor, edgeColourFor } from "../core/palette";
import { drawGroupHull } from "./hull";
import type { GraphControls, GraphRendererProps } from "./GraphRenderer";

// Run ForceAtlas2 synchronously below this node count; above it, fewer iterations
// (still bounded — LineageExplorer caps the revealed slice anyway).
const SYNC_LAYOUT_MAX = 2000;

/** Draw cluster hulls on the screen-space overlay, reusing the canvas hull code. */
function drawHullOverlay(
  sigma: Sigma,
  canvas: HTMLCanvasElement | null,
  graph: Graphology,
  groups: GraphRendererProps["groups"],
): void {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  for (const grp of groups ?? []) {
    const pts: Array<[number, number]> = [];
    for (const id of grp.nodeIds) {
      if (!graph.hasNode(id)) continue;
      const x = graph.getNodeAttribute(id, "x") as number;
      const y = graph.getNodeAttribute(id, "y") as number;
      const vp = sigma.graphToViewport({ x, y });
      pts.push([vp.x, vp.y]);
    }
    if (pts.length > 0) drawGroupHull(ctx, pts, grp.color, 1);
  }
}

export function WebGLRenderer(props: GraphRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const sigmaRef = useRef<Sigma | null>(null);

  // Latest props for imperative Sigma callbacks/reducers — lets us read current
  // highlight/handlers without tearing down + recreating Sigma on every change.
  const propsRef = useRef(props);
  propsRef.current = props;

  const byId = useMemo(() => new Map(props.nodes.map((n) => [n.id, n])), [props.nodes]);
  const byIdRef = useRef(byId);
  byIdRef.current = byId;

  const degreeOf = useMemo(() => {
    const d = new Map<string, number>();
    if (!props.sizeByDegree) return d;
    for (const e of props.edges) {
      d.set(e.source, (d.get(e.source) ?? 0) + 1);
      d.set(e.target, (d.get(e.target) ?? 0) + 1);
    }
    return d;
  }, [props.edges, props.sizeByDegree]);

  // Build a graphology graph from the canonical model. Colours are baked into
  // node/edge attributes; structural or colour changes rebuild + re-layout.
  const graph = useMemo(() => {
    const g = new Graphology({ multi: true, type: "directed", allowSelfLoops: true });
    const total = props.nodes.length;
    props.nodes.forEach((n, i) => {
      if (g.hasNode(n.id)) return;
      const size = props.sizeByDegree
        ? Math.min(10, 2.5 + Math.sqrt(degreeOf.get(n.id) ?? 0) * 1.4)
        : 4;
      const angle = total > 0 ? (i / total) * Math.PI * 2 : 0;
      g.addNode(n.id, {
        label: n.label,
        size,
        color: props.nodeColor?.(n) ?? props.colourForType?.(n.type) ?? colourFor(n.type),
        // Seed on a circle; FA2 overwrites. Deterministic (no Math.random).
        x: Math.cos(angle),
        y: Math.sin(angle),
      });
    });
    for (const e of props.edges) {
      if (!g.hasNode(e.source) || !g.hasNode(e.target)) continue;
      g.addEdge(e.source, e.target, {
        color: props.colourForPredicate?.(e.predicate) ?? edgeColourFor(e.predicate),
        size: 0.6,
      });
    }
    return g;
  }, [
    props.nodes,
    props.edges,
    props.sizeByDegree,
    props.nodeColor,
    props.colourForType,
    props.colourForPredicate,
    degreeOf,
  ]);

  // Lay out + mount Sigma. Recreated when the graph structure/colours change.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || graph.order === 0) return;

    try {
      const settings = forceAtlas2.inferSettings(graph);
      const iterations = graph.order <= SYNC_LAYOUT_MAX ? 300 : 120;
      forceAtlas2.assign(graph, { iterations, settings });
    } catch {
      /* layout is best-effort; nodes still render at their seed positions */
    }

    // labelMode "hover" (default): don't paint static labels for every node —
    // Sigma still shows the hovered node's label (hoverRenderer) and we force a
    // label on the highlighted/hot frontier below. "all" keeps Sigma's default
    // size-thresholded static labels. Hover-only avoids a per-frame text pass.
    const hoverOnly = (propsRef.current.labelMode ?? "hover") === "hover";
    const sigma = new Sigma(graph, container, {
      allowInvalidContainer: true,
      renderLabels: !hoverOnly,
      nodeReducer: (id, data) => {
        const p = propsRef.current;
        const hot = p.highlightIds?.has(id) ?? false;
        if (p.searchActive && !hot) {
          return { ...data, color: "rgba(120,130,150,0.22)", label: "", zIndex: 0 };
        }
        if (hot) {
          return { ...data, size: (data.size as number) + 2, zIndex: 2, forceLabel: true };
        }
        return data;
      },
    });
    sigmaRef.current = sigma;

    sigma.on("clickNode", ({ node }) => {
      const n = byIdRef.current.get(node);
      if (n) propsRef.current.onNodeClick?.(n);
    });
    sigma.on("rightClickNode", ({ node, event }) => {
      event.original.preventDefault();
      const n = byIdRef.current.get(node);
      if (n) propsRef.current.onNodeRightClick?.(n);
    });
    sigma.on("enterNode", ({ node }) => {
      propsRef.current.onNodeHover?.(byIdRef.current.get(node) ?? null);
    });
    sigma.on("leaveNode", () => propsRef.current.onNodeHover?.(null));

    const drawHulls = () => drawHullOverlay(sigma, overlayRef.current, graph, propsRef.current.groups);
    sigma.on("afterRender", drawHulls);
    sigma.getCamera().on("updated", drawHulls);

    const controls: GraphControls = {
      zoomToFit: (durationMs = 400) => {
        sigma.getCamera().animatedReset({ duration: durationMs });
      },
    };
    propsRef.current.onReady?.(controls);

    return () => {
      propsRef.current.onReady?.(null);
      sigma.kill();
      sigmaRef.current = null;
    };
  }, [graph]);

  // Re-render Sigma when highlight/search changes (the reducer reads propsRef).
  useEffect(() => {
    sigmaRef.current?.refresh();
  }, [props.highlightIds, props.searchActive]);

  // Redraw hulls when the groups change (or the graph re-lays out).
  useEffect(() => {
    const s = sigmaRef.current;
    if (s) drawHullOverlay(s, overlayRef.current, graph, props.groups);
  }, [props.groups, graph]);

  // Keep the overlay canvas pixel-sized to the container.
  useEffect(() => {
    const cv = overlayRef.current;
    if (cv) {
      cv.width = props.width;
      cv.height = props.height;
    }
    sigmaRef.current?.refresh();
  }, [props.width, props.height]);

  return (
    <div style={{ position: "relative", width: props.width, height: props.height }}>
      <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
      <canvas
        ref={overlayRef}
        width={props.width}
        height={props.height}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      />
    </div>
  );
}
